import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FormProvider } from 'react-hook-form';

import { analytics } from 'analytics';
import { ReadOnlySankeyChart } from 'components/SankeyChart';
import Button from 'ui/Button';
import Field from 'ui/Field';
import { VStack } from 'ui/Layout';
import Link from 'ui/Link';
import { Text } from 'ui/Typography';
import { formToSankey } from 'utils/form';

import { useApiContext } from '../../../contexts/apiContext';
import { Sankey } from '../../../generated-api';
import { useCurrentUser } from '../../../utils/user';
import { useNewSankeyForm } from '../../SankeyFormProvider';
import SankeyRenderProvider from '../../SankeyRenderProvider';

type Props = {
  draftForm: ReturnType<typeof useNewSankeyForm>;
  onCreateDiagram: (sankey: Sankey) => void;
  onGoBack: () => void;
};

const CreateFromDraftPage: React.FC<Props> = ({
  draftForm,
  onCreateDiagram,
  onGoBack,
}) => {
  const { api } = useApiContext();
  const { refetchUser } = useCurrentUser();

  // Create Sankey diagram from the generated draft.
  const { mutate: createSankey, isLoading: isCreating } = useMutation(
    async () => {
      try {
        const { data: sankey } = await api().sankeys.create({
          sankeyRequest: formToSankey(draftForm),
        });
        await refetchUser();
        onCreateDiagram(sankey);
      } catch (error) {
        analytics.reportError('Error creating diagram from draft', { error });
      }
    }
  );

  return (
    <VStack alignItems="stretch" gap={6} w="100%">
      <FormProvider {...draftForm}>
        <SankeyRenderProvider>
          <ReadOnlySankeyChart />
        </SankeyRenderProvider>
      </FormProvider>
      <Field label="Not happy with this diagram?">
        <Text fontSize="sm">
          <Link
            onClick={() => {
              analytics.track('Smart Import Dialog - Refine - Click');
              onGoBack();
            }}
          >
            Go back and refine
          </Link>{' '}
          your imported files, highlighting the parts that contain useful
          financial data.
        </Text>
      </Field>
      <Button
        variant="primary-solid"
        loading={isCreating}
        onClick={() => {
          analytics.track('Smart Import Dialog - Create Diagram - Click');
          createSankey();
        }}
        w="100%"
      >
        Create this diagram
      </Button>
    </VStack>
  );
};

export default CreateFromDraftPage;
